import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorEvent as SentryErrorEvent, EventHint } from '@sentry/angular';

/**
 * Makes a failed backend call recognisable in Sentry.
 *
 * The ERP answers a rejected call with `{ data, name, statusCode }` — an object
 * with no `message`. Sentry's stock Angular extractor only treats
 * `HttpErrorResponse.error` as an error when both `name` *and* `message` are
 * strings, so this shape falls through every branch to `return error.message`,
 * a plain string:
 *
 *     Http failure response for
 *     https://erp.imt.fi/api/v1/tripPhases/2880/hotels?forReservation=7531577: 409 OK
 *
 * `captureException(<string>)` produces a stackless event whose whole identity
 * is that string, so every HTTP failure in the funnel — every endpoint, every
 * status — landed in one `<unknown>` issue, and the reservation id baked into
 * the URL made even its title meaningless.
 *
 * The fix is to hand Sentry a real `Error` whose message holds only the *stable*
 * part of the failure (status + normalised route) and to attach everything
 * volatile as tags and context. The volatile parts are still what you triage
 * from — the response body carries the backend's actual reason — they just must
 * not decide which issue the event belongs to.
 */

/** What every volatile path segment collapses to in the grouping route. */
const ID_PLACEHOLDER = ':id';

/** Response bodies are attached for triage, so cap them: a stray HTML error
 *  page from a proxy would otherwise push megabytes into every event. */
const MAX_BODY_CHARS = 2000;

/**
 * Fields whose *values* never go to Sentry.
 *
 * `sentry-reporting.ts` states the policy — this is a consumer booking funnel
 * and none of the customer's personal data is sent — and the response body is
 * the one place it could leak in by accident. A `ValidationError` is keyed by
 * the field it rejected, and the backend does interpolate the submitted value
 * into the message it pairs with that key (`reservation-api-public.js:400`
 * builds `'invalid query parameter: withHotel=' + req.query.withHotel`). The
 * values it interpolates today happen to be ids, so nothing leaks right now —
 * but that is incidental, and the same pattern on a passenger or
 * customer-account field would put a name, an email or a phone number into
 * every matching issue.
 *
 * Keys, not values, are what triage actually needs: knowing the backend
 * rejected `email` is the useful part, and the address itself adds nothing.
 *
 * Drawn from the shapes the funnel submits — `Person`, `PersonExtraProps` and
 * `Passenger` in `reservation.service.ts`, plus `CreateCustomerAccountInput` in
 * `auth.service.ts`, where `uid` IS the phone number and `token` is the SMS
 * login code. This is a denylist, so it fails open: extend it when a new
 * personal field starts crossing the wire.
 */
const REDACTED_KEYS = new Set([
  // Person
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'dateOfBirth',
  // PersonExtraProps
  'gender',
  'nationality',
  'clubOne',
  'address',
  'postNumber',
  'city',
  'companyName',
  'businessId',
  // `Person.bonus` — the customer's loyalty balance. Money they hold with us,
  // and nothing triage needs: that a bonus write was rejected is the useful
  // part, the balance itself is not.
  'bonus',
  // Passenger
  'ownCarPlateNumber',
  'nickname',
  // Customer-account sign-in — `uid` is the phone number, `token` the SMS code
  'uid',
  'token',
]);

const REDACTED_PLACEHOLDER = '[redacted]';

/** Everything worth knowing about a failed request that must NOT influence
 *  which Sentry issue the event groups into. */
export interface HttpFailureDetails {
  /** HTTP status, or 0 when the request never got an answer. */
  status: number;
  /** Grouping route — path only, numeric ids replaced. */
  route: string;
  /** The real URL as requested, query string included. */
  url: string;
  statusText: string;
  /** The parsed response body — for this backend `{ data, name, statusCode }`. */
  body: unknown;
}

/**
 * The `Error` handed to Sentry in place of an `HttpErrorResponse`.
 *
 * A distinct class rather than a bare `Error` so `beforeSend` can recognise it
 * off `hint.originalException` and unpack {@link HttpFailureDetails} into tags,
 * context and a fingerprint — `captureException` gives us no other way to reach
 * the event from inside Angular's `ErrorHandler`.
 */
export class HttpRequestError extends Error {
  constructor(
    message: string,
    readonly details: HttpFailureDetails,
  ) {
    super(message);
    // Sentry shows `name` as the issue's exception type. Without this it reads
    // "Error", which says nothing about what kind of failure it was.
    this.name = 'HttpRequestError';
  }
}

/**
 * Path of a request URL with every id collapsed to `:id`.
 *
 * Both halves matter. Keeping the query string would give every reservation its
 * own issue (`?forReservation=7531577`), and so would keeping the raw ids in the
 * path — the opposite failure from the one this file exists to fix, and a worse
 * one, because it buries a recurring bug under thousands of one-event issues.
 */
export function normalizeRequestPath(url: string): string {
  return pathnameOf(url)
    .split('/')
    .map((segment) => (/^\d+$/.test(segment) ? ID_PLACEHOLDER : segment))
    .join('/');
}

/** The grouping message: status plus route, nothing that varies per request. */
function httpFailureMessage(status: number, route: string): string {
  // Angular reports a request that never got an answer — offline, DNS, CORS,
  // aborted — as status 0. That is a different failure from any server reply
  // and has to group on its own rather than as "HTTP 0".
  return status === 0 ? `HTTP network error ${route}` : `HTTP ${status} ${route}`;
}

/** Builds the `Error` that stands in for a failed request. */
export function toHttpRequestError(response: HttpErrorResponse): HttpRequestError {
  // `url` is null when the request was built without one at all; nothing in the
  // funnel does that, but the type allows it and an empty route would produce a
  // message with a dangling space.
  const url = response.url ?? '';
  const route = url === '' ? '<unknown url>' : normalizeRequestPath(url);
  return new HttpRequestError(httpFailureMessage(response.status, route), {
    status: response.status,
    route,
    url,
    statusText: response.statusText,
    body: response.error,
  });
}

/**
 * `ErrorHandlerOptions.extractor` for `Sentry.createErrorHandler`.
 *
 * Only `HttpErrorResponse` is handled here; everything else — real `Error`s from
 * component code, rejected promises, strings — is already grouped correctly by
 * the SDK's own extractor, so it keeps that behaviour.
 */
export function sentryErrorExtractor(
  error: unknown,
  defaultExtractor: (error: unknown) => unknown,
): unknown {
  return error instanceof HttpErrorResponse ? toHttpRequestError(error) : defaultExtractor(error);
}

/**
 * `Sentry.init`'s `beforeSend`: unpacks an {@link HttpRequestError} onto the
 * event it produced.
 *
 * The fingerprint is the load-bearing part. Every `HttpRequestError` is
 * constructed in this file, so all of them share the same top stack frames — and
 * Sentry groups on the stack whenever there is one, which would re-merge every
 * HTTP failure into a single issue despite the careful message. Setting the
 * fingerprint explicitly is what actually makes the grouping status+route based.
 */
export function attachHttpFailureContext(
  event: SentryErrorEvent,
  hint: EventHint,
): SentryErrorEvent {
  const original = hint.originalException;
  if (!(original instanceof HttpRequestError)) return event;

  const { status, route, url, statusText, body } = original.details;
  // Set by `reportFailure` at the call sites that swallow a failure. Two
  // different swallowed operations hitting the same endpoint are different
  // bugs, so it belongs in the fingerprint as well as in the tags.
  const operation = event.tags?.['operation'];
  const errorName = backendErrorName(body);

  event.tags = {
    ...event.tags,
    'http.status': String(status),
    'http.route': route,
    ...(errorName ? { 'http.error_name': errorName } : {}),
  };
  event.contexts = {
    ...event.contexts,
    http_response: {
      url,
      status,
      status_text: statusText,
      body: describeBody(body),
    },
  };
  // 4xx are the backend refusing a request, which is worth seeing but is not the
  // app falling over; 5xx and "no answer at all" are. Reserving `error` for the
  // latter keeps a Sentry alert on error-level events meaningful.
  event.level = status === 0 || status >= 500 ? 'error' : 'warning';
  event.fingerprint = [
    'http',
    String(status),
    route,
    ...(typeof operation === 'string' ? [operation] : []),
  ];
  return event;
}

function pathnameOf(url: string): string {
  try {
    // `URL` needs an absolute input. Deployed builds request absolute URLs via
    // `environment.erpBaseUrl`, but local dev leaves that empty and requests
    // `/api/...` through the dev-server proxy — a base makes both parse alike.
    return new URL(url, 'http://funnel.invalid').pathname;
  } catch {
    // A URL the platform refuses to parse must not take down the error handler
    // itself; grouping on the raw string is still better than losing the event.
    return url;
  }
}

function backendErrorName(body: unknown): string | undefined {
  const name = (body as { name?: unknown } | null)?.name;
  return typeof name === 'string' ? name : undefined;
}

function describeBody(body: unknown): string | null {
  if (body == null) return null;
  const text = typeof body === 'string' ? body : safeStringify(body);
  return text.length > MAX_BODY_CHARS ? `${text.slice(0, MAX_BODY_CHARS)}…` : text;
}

function safeStringify(body: unknown): string {
  try {
    // The replacer runs at every depth, so a personal field nested inside a
    // `data` object is redacted just as a top-level one is.
    return (
      JSON.stringify(body, (key, value) =>
        REDACTED_KEYS.has(key) ? REDACTED_PLACEHOLDER : value,
      ) ?? String(body)
    );
  } catch {
    // Circular, or a DOM object like the `ProgressEvent` Angular hands back on a
    // network failure. Naming the shape is still worth more than nothing.
    return Object.prototype.toString.call(body);
  }
}
